import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LogoutIcon from '@mui/icons-material/Logout';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PaidIcon from '@mui/icons-material/Paid';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import InboxIcon from '@mui/icons-material/Inbox';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { useRole } from '../auth/RoleContext';
import { canAccess } from '../auth/permissions';
import { auth } from './firebaseConfig';
import Brand from './components/Brand';
import { useT } from '../i18n/LanguageContext';
import LanguageToggle from '../i18n/LanguageToggle';

// Height of the phone bottom bar; the page body is padded by this (plus the iPhone home-indicator
// inset) so the last card on a screen is never hidden behind it.
const BOTTOM_NAV_HEIGHT = 64;

export default function NavigationMenu() {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [mastersOpen, setMastersOpen] = useState(false);
    const [mastersAnchor, setMastersAnchor] = useState(null);
    const [accountAnchor, setAccountAnchor] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { role, user: appUser } = useRole();
    const t = useT();

    const fullName = [appUser?.firstName, appUser?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
    const userLabel = fullName || appUser?.email || 'Signed in';

    const toggleDrawer = (open) => () => {
        setDrawerOpen(open);
    };

    const handleNavigation = (path) => {
        navigate(path);
        setDrawerOpen(false);
        setMastersAnchor(null);
        setAccountAnchor(null);
    };

    const handleLogout = async () => {
        setDrawerOpen(false);
        setMastersAnchor(null);
        setAccountAnchor(null);
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const allMenuItems = [
        { text: t('nav.scanTickets'), short: t('nav.scan'), icon: <HomeIcon />, path: '/ScanTicket', topBar: true },
        { text: t('nav.tickets'), short: t('nav.tickets'), icon: <InfoIcon />, path: '/Ticket', topBar: true },
        { text: t('nav.invoicing'), short: t('nav.invoicing'), icon: <ReceiptIcon />, path: '/Invoicing', topBar: true },
        // The two originals stay reachable by URL but are off the bar — one entry point now.
        { text: t('nav.createInvoice'), short: 'Create', icon: <ContactMailIcon />, path: '/CreateInvoice', topBar: false },
        { text: t('nav.settlements'), short: 'Settlements', icon: <ReceiptIcon />, path: '/IvyInvoice', topBar: false },
        { text: t('nav.plantInvoice'), short: 'Plant Inv', icon: <ReceiptIcon />, path: '/IvyPlantInvoice', topBar: true },
        // Field Tickets list stays hidden for now; the Field Ticket Invoice (Ivy's → Williams Brothers) is enabled.
        // { text: 'Field Tickets', short: 'Field', icon: <RuleIcon />, path: '/FieldTickets', topBar: true },
        { text: t('nav.fieldTicketInvoice'), short: 'Field Inv', icon: <ReceiptIcon />, path: '/FieldTicketInvoice', topBar: true },
        { text: t('nav.weeklyInvoice'), short: 'Weekly', icon: <ReceiptLongIcon />, path: '/invoices/weekly', topBar: true },
        { text: t('nav.weeklySummary'), short: 'Summary', icon: <AssessmentIcon />, path: '/WeeklySummary', topBar: true },
        { text: t('nav.weeklyRecon'), short: 'P&L', icon: <AssessmentIcon />, path: '/WeeklyRecon', topBar: true },
        { text: t('nav.ticketReport'), short: 'Detail', icon: <AssessmentIcon />, path: '/TicketReport', topBar: true },
        { text: t('nav.ticketIntake'), short: 'Intake', icon: <InboxIcon />, path: '/TicketIntake', topBar: true },
        { text: t('nav.fuelExpenses'), short: 'Fuel', icon: <LocalGasStationIcon />, path: '/FuelExpenses', topBar: true },
        { text: t('nav.flatbedInvoice'), short: 'Flatbed', icon: <ReceiptIcon />, path: '/FlatbedInvoice', topBar: true },
        { text: t('nav.payroll'), short: t('nav.payroll'), icon: <PaidIcon />, path: '/Payroll', topBar: true },
        { text: t('nav.reconciliation'), short: 'Recon', icon: <AccountBalanceIcon />, path: '/Reconciliation', topBar: true },
        // Verify is intentionally NOT a nav item — it's reached via the "Verify flagged" button on the
        // Tickets screen (route + permission still exist so that deep-link works).
        // { text: 'Verify', short: 'Verify', icon: <RuleIcon />, path: '/Verify', topBar: true },
        { text: t('nav.ocrTool'), short: 'OCR', icon: <DocumentScannerIcon />, path: '/Admin/Ocr', topBar: false },
        { text: t('nav.users'), short: 'Users', icon: <ManageAccountsIcon />, path: '/Admin/Users', topBar: false },
        { text: t('nav.billing'), short: t('nav.billing'), icon: <CreditCardIcon />, path: '/Billing', topBar: false },
    ];

    const allMastersItems = [
        { text: t('nav.drivers'), path: '/Masters/Drivers' },
        { text: t('nav.carriers'), path: '/Masters/Carriers' },
        { text: t('nav.trucks'), path: '/Masters/Trucks' },
        { text: t('nav.plants'), path: '/Masters/Plants' },
        { text: t('nav.subContractors'), path: '/Masters/Companies' },
        // Hidden for now (2026-07-08) — restore to re-enable the Vendors master screen.
        // { text: 'Vendors', path: '/Masters/Vendors' },
        { text: t('nav.rateCards'), path: '/Masters/RateCards' },
        { text: t('nav.plantPaymentConfig'), path: '/Masters/PlantPaymentConfigs' },
        { text: t('nav.hauledForRules'), path: '/Masters/HauledForRules' },
    ];

    // Screen gating: only show what this role may access (null role = still loading -> show nothing yet).
    const menuItems = allMenuItems.filter((m) => role && canAccess(role, m.path));
    const mastersItems = allMastersItems.filter((m) => role && canAccess(role, m.path));

    // Phone bottom bar: the three everyday jobs, plus "More" for everything else (opens the drawer).
    const bottomItems = [
        { label: t('nav.scan'), icon: <PhotoCameraIcon />, path: '/ScanTicket' },
        { label: t('nav.tickets'), icon: <ListAltIcon />, path: '/Ticket' },
        { label: t('nav.invoices'), icon: <ReceiptIcon />, path: '/Invoicing' },
    ].filter((m) => role && canAccess(role, m.path));
    const bottomValue = bottomItems.some((m) => m.path === location.pathname) ? location.pathname : 'more';

    return (
        <div>
            <GlobalStyles
                styles={(theme) => ({
                    [theme.breakpoints.down('md')]: {
                        body: { paddingBottom: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))` },
                    },
                })}
            />
            <AppBar position="static">
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        onClick={toggleDrawer(true)}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Brand nameColor="inherit" fontSize={17} />
                    </Box>
                    {/* The full button row only fits a wide screen; phones use the bottom bar + drawer. */}
                    <Box sx={{ display: { xs: 'none', lg: 'flex' } }}>
                        {menuItems.filter((m) => m.topBar).map((m) => (
                            <Button key={m.path} color="inherit" onClick={() => handleNavigation(m.path)}>{m.short}</Button>
                        ))}
                        {mastersItems.length > 0 && (
                            <Button color="inherit" onClick={(e) => setMastersAnchor(e.currentTarget)}>{t('nav.masters')}</Button>
                        )}
                    </Box>
                    <Menu
                        anchorEl={mastersAnchor}
                        open={Boolean(mastersAnchor)}
                        onClose={() => setMastersAnchor(null)}
                    >
                        {mastersItems.map((m) => (
                            <MenuItem key={m.path} onClick={() => handleNavigation(m.path)}>{m.text}</MenuItem>
                        ))}
                    </Menu>
                    <LanguageToggle onDark sx={{ ml: 1 }} />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            ml: 1.5,
                            minWidth: 0,
                            cursor: 'pointer',
                        }}
                        title={`${userLabel}${role ? ` (${role})` : ''}`}
                        onClick={(e) => setAccountAnchor(e.currentTarget)}
                    >
                        <AccountCircleIcon />
                        <Box sx={{ display: { xs: 'none', lg: 'block' }, minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 180, lineHeight: 1.2 }}
                            >
                                {userLabel}
                            </Typography>
                            {role && (
                                <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{ display: 'block', opacity: 0.8, lineHeight: 1.2 }}
                                >
                                    {role.replaceAll('_', ' ')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Menu
                        anchorEl={accountAnchor}
                        open={Boolean(accountAnchor)}
                        onClose={() => setAccountAnchor(null)}
                    >
                        <MenuItem disabled>
                            <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary={userLabel}
                                secondary={role ? role.replaceAll('_', ' ') : undefined}
                            />
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary={t('nav.logout')} />
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
            >
                <div role="presentation" onKeyDown={toggleDrawer(false)}>
                    <List sx={{ minWidth: 260, pb: 'env(safe-area-inset-bottom)' }}>
                        {menuItems.map((item) => (
                            <ListItem button key={item.path} onClick={() => handleNavigation(item.path)}>
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItem>
                        ))}

                        {mastersItems.length > 0 && (
                            <>
                                <ListItem button onClick={() => setMastersOpen((v) => !v)}>
                                    <ListItemIcon><SettingsIcon /></ListItemIcon>
                                    <ListItemText primary={t('nav.masters')} />
                                    {mastersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </ListItem>
                                <Collapse in={mastersOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {mastersItems.map((m) => (
                                            <ListItem
                                                button
                                                key={m.path}
                                                sx={{ pl: 6 }}
                                                onClick={() => handleNavigation(m.path)}
                                            >
                                                <ListItemText primary={m.text} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </>
                        )}

                        <ListItem button onClick={handleLogout}>
                            <ListItemIcon><LogoutIcon /></ListItemIcon>
                            <ListItemText primary={t('nav.logout')} />
                        </ListItem>
                    </List>
                </div>
            </Drawer>

            <Paper
                elevation={8}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: (theme) => theme.zIndex.appBar,
                    pb: 'env(safe-area-inset-bottom)',
                }}
            >
                <BottomNavigation
                    showLabels
                    value={bottomValue}
                    sx={{ height: BOTTOM_NAV_HEIGHT }}
                    onChange={(e, value) => (value === 'more' ? setDrawerOpen(true) : handleNavigation(value))}
                >
                    {bottomItems.map((m) => (
                        <BottomNavigationAction key={m.path} value={m.path} label={m.label} icon={m.icon} />
                    ))}
                    <BottomNavigationAction value="more" label={t('nav.more')} icon={<MoreHorizIcon />} />
                </BottomNavigation>
            </Paper>
        </div>
    );
}
